import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ICellRendererParams } from 'ag-grid-community';
import type { ICellRendererAngularComp } from 'ag-grid-angular';
import { GapOperation } from '../../../permissions/gap-permissions';
import { GapAccessService } from '../../../services/gap-access/gap-access.service';

export interface Action {
  icon?: string;
  label?: string;
  onClick?: (params: GainsActionCellComponentParams) => void;
  divider?: boolean;
  disabled?: (params: GainsActionCellComponentParams) => boolean;
  show?: (params: GainsActionCellComponentParams) => boolean;
  /** The GAP operation the action runs: disabled, the reason on hover, when the user's level does not allow it. */
  requires?: GapOperation;
}

export interface GainsActionCellComponentParams extends ICellRendererParams {
  actions: Action[];
}

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gains-action-cell.component.html',
  styleUrl: './gains-action-cell.component.scss'
})
export class GainsActionCellComponent implements ICellRendererAngularComp {
  params!: GainsActionCellComponentParams;
  actions: Action[] = [];

  constructor(
    private readonly _cdr: ChangeDetectorRef,
    private readonly _gapAccessService: GapAccessService
  ) {
    // The level can change while the grid shows: the menu follows it
    this._gapAccessService.access$.pipe(takeUntilDestroyed()).subscribe(() => this._cdr.markForCheck());
  }

  agInit(params: GainsActionCellComponentParams): void {
    this.params = params;
    this.actions = params?.actions || [];
  }

  refresh(params: GainsActionCellComponentParams): boolean {
    this.params = params;
    this.actions = params.actions || [];
    // ag-grid refreshes cells imperatively, outside Angular's binding graph, so an
    // OnPush view would keep rendering the previous row's menu (e.g. still offering
    // "Disable" on a row that was just disabled). Mark it dirty explicitly.
    this._cdr.markForCheck();
    return true;
  }

  shouldShowAction(params: GainsActionCellComponentParams, item: Action): boolean {
    if (item.show) {
      return item.show(params);
    }

    return true;
  }

  shouldDisableAction(params: GainsActionCellComponentParams, item: Action): boolean {
    if (this.deniedReason(item)) {
      return true;
    }
    if (item.disabled) {
      return item.disabled(params);
    }

    return false;
  }

  /** Why the user may not run the action, for its hover; null when they may. */
  deniedReason(item: Action): string | null {
    return item.requires ? this._gapAccessService.denial(item.requires) : null;
  }

  /** Runs the action, unless it is disabled: a disabled menu item still gets the click and the keys. */
  run(params: GainsActionCellComponentParams, item: Action): void {
    if (!this.shouldDisableAction(params, item)) {
      item.onClick?.(params);
    }
  }
}
